import HeroSection from "./components/HeroSection";
import ProblemSection from "./components/ProblemSection";
import WhyDifferentSection from "./components/WhyDifferentSection";
import ProcessingSpeedsSection from "./components/ProcessingSpeedsSection";
import WhoIsThisForSection from "./components/WhoIsThisForSection";
import HowItWorksSection from "./components/HowItWorksSection";
import CostComparisonSection from "./components/CostComparisonSection";
import FAQSection from "./components/FAQSection";
import FinalCTASection from "./components/FinalCTASection";
import RelatedPassportServices from "@/components/feature/RelatedPassportServices";

const pageUrl = "https://fastpassportcenter.com/passport-services/expedited-passport-service/";

const faqSchemaItems = [
  {
    "@type": "Question",
    name: "What is an expedited passport service?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "An expedited passport service is a private registered courier that submits your passport application directly to a U.S. federal passport agency on your behalf — bypassing standard mail processing. Unlike applying through the post office, a registered courier like Fast Passport Center has reserved daily submission slots at passport agencies, allowing your application to be processed in as little as 3 business days instead of the standard 4–6 weeks.",
    },
  },
  {
    "@type": "Question",
    name: "Is a passport expediting service legitimate?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Yes — but only if the company is registered with the U.S. Department of State. The State Dept maintains an official list of registered passport couriers. Fast Passport Center has been on that list for over 20 years. Always verify a company is on the official registry before using their service. Unregistered companies cannot submit applications directly to passport agencies.",
    },
  },
  {
    "@type": "Question",
    name: "How fast can I get my passport with an expediting service?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Fast Passport Center offers processing in as little as 3 business days from when we receive your documents. Add approximately 1–2 days for your documents to reach us (if mailing) and 1 day for overnight return delivery. Total door-to-door time is typically 5–7 business days for the fastest tier.",
    },
  },
  {
    "@type": "Question",
    name: "Why is Fast Passport Center cheaper than FedEx or Staples?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "FedEx Office and Staples are resellers — they charge you a service fee and then send your application to a registered courier like Fast Passport Center. You pay their fee plus the courier's fee. When you use Fast Passport Center directly, you eliminate the middleman and pay only our direct courier fee — typically 20–80% less than retail passport service counters.",
    },
  },
  {
    "@type": "Question",
    name: "Do I still have to pay government fees?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Yes. Government fees are paid directly to the U.S. Department of State and are required regardless of which service you use. Fast Passport Center's service fee is separate and covers document review, courier handling, and overnight return delivery. We do not mark up government fees.",
    },
  },
  {
    "@type": "Question",
    name: "Can I get an expedited passport without going to a passport agency?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Yes — for passport renewals (DS-82), you never need to visit a passport agency or acceptance facility. You mail us your documents and we handle everything. For new passport applications (DS-11) and child passports, an in-person visit to a passport acceptance facility (like a post office or courthouse) is required by law before we can submit your application — but you do not need to go to a regional passport agency yourself.",
    },
  },
  {
    "@type": "Question",
    name: "What if I live far from a passport agency?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "That's exactly why services like Fast Passport Center exist. Cities like Tampa, Charlotte, Nashville, Louisville, Sacramento, Kansas City, and Bozeman don't have a nearby regional passport agency — the nearest one may be 4–8 hours away. Our courier handles the agency trip for you. You mail us your documents; we handle the rest.",
    },
  },
  {
    "@type": "Question",
    name: "How is expedited passport service different from government expedited processing?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Government expedited processing (the $60 fee paid to the State Dept) shortens processing to 2–3 weeks — but that doesn't include mailing time, which can add another 2 weeks. A registered courier service like Fast Passport Center physically hand-delivers your application to the agency with reserved daily submission slots, cutting total processing time to as little as 3 business days — far faster than what the $60 government expedite fee alone can achieve.",
    },
  },
  {
    "@type": "Question",
    name: "What documents do I need for expedited passport service?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Requirements vary by service type. For renewal: your current passport, Form DS-82, one passport photo, and proof of travel. For a new passport: birth certificate, government-issued ID, Form DS-11 (completed at an acceptance facility), one passport photo, and proof of travel. For a child passport: both parents' consent, birth certificate, Form DS-11, and proof of travel. Fast Passport Center provides a personalized document checklist when you start your order.",
    },
  },
  {
    "@type": "Question",
    name: "Can I track my passport application?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Yes. Every Fast Passport Center order includes a personal tracking dashboard with real-time status updates from submission through return delivery. You'll also receive email and text updates at each major milestone.",
    },
  },
];

const serviceSchema = {
  "@type": "Service",
  name: "Expedited Passport Service",
  provider: {
    "@type": "Organization",
    name: "Fast Passport Center",
    url: "https://fastpassportcenter.com",
    telephone: "1-877-253-0084",
    address: {
      "@type": "PostalAddress",
      streetAddress: "1629 K Street, Suite 300",
      addressLocality: "Washington",
      addressRegion: "DC",
      postalCode: "20006",
    },
  },
  description: "Expedited passport service from a registered US Department of State passport courier. Processing in as little as 3 business days. A+ BBB rated, 20+ years experience.",
  areaServed: "US",
  offers: {
    "@type": "Offer",
    price: "195",
    priceCurrency: "USD",
    description: "Expedited passport service from a registered courier ranging from $195 to $345",
  },
};

const breadcrumbSchema = {
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com" },
    { "@type": "ListItem", position: 2, name: "Passport Services", item: "https://fastpassportcenter.com/passport-services/" },
    { "@type": "ListItem", position: 3, name: "Expedited Passport Service", item: pageUrl },
  ],
};

const graph = [
  {
    "@type": "Organization",
    name: "Fast Passport Center",
    url: "https://fastpassportcenter.com",
    telephone: "+1-877-253-0084",
    email: "contactus@fastpassportcenter.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "1629 K Street, Suite 300",
      addressLocality: "Washington",
      addressRegion: "DC",
      postalCode: "20006",
      addressCountry: "US",
    },
    areaServed: { "@type": "Country", name: "United States" },
    priceRange: "$$$",
    openingHours: ["Mo-Fr 09:00-18:00"],
    aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
  },
  serviceSchema,
  breadcrumbSchema,
  { "@type": "FAQPage", mainEntity: faqSchemaItems },
];

const jsonLd = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });

function ExpeditedPassportSEOHead() {
  return (
    <>
      <title>Expedited Passport Service — Registered US Courier | Fast Passport Center</title>
      <meta name="description" content="Fast Passport Center is a registered US Department of State passport courier offering expedited passport service in as little as 3 business days. Not a reseller. Direct courier pricing — 20–80% less than FedEx or Staples. A+ BBB rated, 20+ years." />
      <meta name="keywords" content="expedited passport service, passport expediting service, passport expediter, registered passport courier, fast passport, emergency passport, urgent passport, passport courier service, expedited passport processing" />
      <meta name="last-modified" content="2026-07-01" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Expedited Passport Service | Direct Registered Courier" />
      <meta property="og:description" content="Skip the FedEx markup. Fast Passport Center is a registered State Dept courier — not a middleman. Expedited passport service from $195. A+ BBB, 20+ years." />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content="Fast Passport Center" />
      <meta property="og:image" content="https://fastpassportcenter.com/og-image.jpg" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Expdited Passport Service | Direct Registered Courier" />
      <meta name="twitter:description" content="Skip the FedEx markup. Fast Passport Center is a registered State Dept courier — not a middleman. Expedited passport service from $195. A+ BBB, 20+ years." />
      <meta name="twitter:image" content="https://fastpassportcenter.com/og-image.jpg" />
      <link rel="canonical" href={pageUrl} />
      <script type="application/ld+json" id="structured-data-expedited-passport" dangerouslySetInnerHTML={{ __html: jsonLd }} />
    </>
  );
}

export default function ExpeditedPassportServicePage() {
  return (
    <div className="font-sans">
      <ExpeditedPassportSEOHead />
      <HeroSection />
      <ProblemSection />
      <WhyDifferentSection />
      <ProcessingSpeedsSection />
      <WhoIsThisForSection />
      <HowItWorksSection />
      <CostComparisonSection />
      <FAQSection />

      <RelatedPassportServices
        services={[
          {
            title: "Fast Passport Renewal",
            description: "Just need to renew your passport? No office visit required. As fast as 3 business days.",
            link: "/passport-services/fast-passport-renewal/",
            icon: "ri-refresh-line",
          },
          {
            title: "Urgent Passport Service",
            description: "Trip under 2 weeks away? Timeline-based options to match your exact travel date.",
            link: "/passport-services/urgent-passport/",
            icon: "ri-timer-line",
          },
          {
            title: "Emergency Passport",
            description: "Traveling in less than 72 hours? Emergency processing as fast as 24 hours available.",
            link: "/passport-services/emergency-passport/",
            icon: "ri-alert-line",
          },
          {
            title: "Passport Expediting Service",
            description: "The full story on how registered couriers work, and why we're 20-80% less than retail.",
            link: "/passport-services/passport-expediting-service/",
            icon: "ri-government-line",
          },
        ]}
        colorClass="text-[#0F766E]"
        bgClass="bg-[#F0FAF9]"
        borderClass="border-[#D2EEEB]"
        linkColorClass="text-[#0F766E]"
      />

      <FinalCTASection />

      {/* Footer */}
      <footer className="bg-[#0C4A47] text-gray-300 py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Fast Passport Center</h4>
              <p className="text-gray-400 text-sm leading-relaxed mb-3">
                Registered U.S. Department of State passport courier. A+ BBB rated. 20+ years of passport and visa experience.
              </p>
              <p className="text-gray-500 text-xs">
                1629 K Street, Suite 300<br />Washington, DC 20006
              </p>
            </div>
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/passport-services/expedited-passport-service/" className="hover:text-white transition-colors cursor-pointer">Expedited Passport Service</a></li>
                <li><a href="/passport-services/fast-passport-renewal/" className="hover:text-white transition-colors cursor-pointer">Fast Passport Renewal</a></li>
                <li><a href="/passport-services/urgent-passport/" className="hover:text-white transition-colors cursor-pointer">Urgent Passport</a></li>
                <li><a href="/passport-services/emergency-passport/" className="hover:text-white transition-colors cursor-pointer">Emergency Passport</a></li>
                <li><a href="/passport-services/new-passport/" className="hover:text-white transition-colors cursor-pointer">New Passport</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="tel:8772530084" className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">877-253-0084</a></li>
                <li><a href="mailto:contactus@fastpassportcenter.com" className="hover:text-white transition-colors cursor-pointer">Contact Us</a></li>
                <li><a href="/helpful-resources/" className="hover:text-white transition-colors cursor-pointer">Helpful Resources</a></li>
                <li><a href="/helpful-resources/passport-processing-times/" className="hover:text-white transition-colors cursor-pointer">Processing Times</a></li>
                <li><a href="/helpful-resources/passport-fees/" className="hover:text-white transition-colors cursor-pointer">Passport Fees</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Trust &amp; Credentials</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center gap-2">
                  <i className="ri-shield-check-line"></i> A+ BBB Accredited
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-government-fill"></i> Registered State Dept. Courier
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-history-line"></i> 20+ Years Experience
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-lock-line"></i> Secure &amp; Confidential
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-[#3E8D89] pt-8 text-center text-xs text-gray-500">
            <p>&copy; {new Date().getFullYear()} Fast Passport Center. All rights reserved. Fast Passport Center is a private passport courier service registered with the U.S. Department of State. We are not a government agency.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}